import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import Button from '../../components/ui/Button';
import '../../styles/AdminEntity.css';

// ============================================================
// Teszt-labor — fiktív adat generálás és workflow-szimuláció
// (kizárólag superadmin). Minden művelet csak is_test jelölésű
// adatot érint; a backend garantálja a valódi adatok védelmét.
// ============================================================

const GENERATORS = [
  { kind: 'users',      icon: '👤', max: 50, hasRole: true },
  { kind: 'properties', icon: '🏡', max: 50 },
  { kind: 'tickets',    icon: '🎫', max: 50 },
  { kind: 'partners',   icon: '🤝', max: 50 },
  { kind: 'contracts',  icon: '📄', max: 50 },
  { kind: 'agent-activity', icon: '🤖', max: 50 },
  { kind: 'user-activity',  icon: '🙋', max: 50 },
];
const DELETE_CATEGORIES = ['tickets', 'contracts', 'properties', 'partners', 'users', 'all'];

// Ez a két típus a háttérben fut (2 mp/lépés) — élő progress-pollozással,
// nem szinkron válasszal, lásd testDataController.js generate().
const PROGRESS_KINDS = new Set(['agent-activity', 'user-activity']);
const POLL_INTERVAL_MS = 700;

const TestLabPage = () => {
  const { api } = useAuth();
  const { t } = useI18n();

  const [stats, setStats] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(null);          // futó művelet kind-ja
  const [counts, setCounts] = useState(Object.fromEntries(GENERATORS.map(g => [g.kind, 5])));
  const [userRole, setUserRole] = useState('user');
  const [delCategory, setDelCategory] = useState('tickets');
  const [error, setError] = useState('');
  // Élő progress a háttérben futó agent-/user-activity job-hoz: { kind, done, total, lastLine }
  const [progress, setProgress] = useState(null);
  const pollTimer = React.useRef(null);

  // Folyamatos "mixed load" szimuláció: { runId, durationMs, elapsedMs, status, counts, stopRequested }
  const [sim, setSim] = useState(null);
  const [simMinutes, setSimMinutes] = useState(2);
  const simPollTimer = React.useRef(null);
  const simLogSeen = React.useRef(0); // hány sort láttunk már a job.log-ból (streameléshez)

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    if (simPollTimer.current) clearTimeout(simPollTimer.current);
  }, []);

  const appendLog = (lines, header) => {
    const stamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLog(prev => [`── ${stamp} ${header} ──`, ...(lines || []), ...prev].slice(0, 400));
  };

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get('/admin/test-data/stats');
      setStats(r.data);
    } catch { setError(t('testlab.loadFailed')); }
  }, [api, t]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Ha egy korábban indított szimuláció még mindig fut a szerveren (pl.
  // elnavigáltunk az oldalról és a React state elveszett), csatlakozzunk
  // hozzá ahelyett, hogy elfelejtenénk és újat indíthatnánk mellé.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/test-data/simulate/active');
        if (r.data.runId) {
          simLogSeen.current = 0;
          pollSimulation(r.data.runId);
        }
      } catch { /* nincs mit tenni — legfeljebb nem ismerjük fel az aktív futást */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Az agent-/user-activity job élő státuszát pollozza, amíg le nem fut.
  const pollProgress = (kind, runId) => {
    const tick = async () => {
      try {
        const r = await api.get(`/admin/test-data/progress/${runId}`);
        const d = r.data;
        setProgress({ kind, done: d.done, total: d.total, lastLine: d.log?.[d.log.length - 1] });
        if (d.status === 'running') {
          pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
        appendLog(d.log, t(`testlab.kinds.${kind}`));
        if (d.status === 'done') setStats(d.stats);
        else setError(d.error || t('testlab.runFailed'));
        setProgress(null);
        setBusy(null);
      } catch {
        setError(t('testlab.runFailed'));
        setProgress(null);
        setBusy(null);
      }
    };
    tick();
  };

  const runGenerate = async (kind) => {
    setBusy(kind); setError('');

    if (PROGRESS_KINDS.has(kind)) {
      try {
        const r = await api.post('/admin/test-data/generate', { kind, count: counts[kind] });
        setProgress({ kind, done: 0, total: r.data.total, lastLine: '' });
        pollProgress(kind, r.data.runId);
      } catch (e) {
        setError(e.response?.data?.error || t('testlab.runFailed'));
        setBusy(null);
      }
      return;
    }

    try {
      const payload = { kind, count: counts[kind] };
      if (kind === 'users') payload.role = userRole;
      const r = await api.post('/admin/test-data/generate', payload);
      appendLog(r.data.log, t(`testlab.kinds.${kind}`));
      setStats(r.data.stats);
    } catch (e) {
      appendLog(e.response?.data?.log, t(`testlab.kinds.${kind}`));
      setError(e.response?.data?.error || t('testlab.runFailed'));
    } finally { setBusy(null); }
  };

  // A szimuláció progressét pollozza; az új log-sorokat élőben belefolyatja
  // a fő naplópanelbe (nem várja meg a végét, mert ez akár 5 percig is futhat).
  const pollSimulation = (runId) => {
    const tick = async () => {
      try {
        const r = await api.get(`/admin/test-data/progress/${runId}`);
        const d = r.data;
        const freshLines = (d.log || []).slice(simLogSeen.current);
        if (freshLines.length) {
          setLog(prev => [...[...freshLines].reverse(), ...prev].slice(0, 400));
          simLogSeen.current = d.log.length;
        }
        setSim({
          runId, durationMs: d.durationMs, elapsedMs: d.elapsedMs,
          status: d.status, counts: d.counts, stopRequested: d.stopRequested,
        });
        if (d.status === 'running') {
          simPollTimer.current = setTimeout(tick, 1000);
          return;
        }
        if (d.status === 'error') setError(d.error || t('testlab.runFailed'));
        if (d.stats) setStats(d.stats);
      } catch {
        setError(t('testlab.runFailed'));
      }
    };
    tick();
  };

  const startSimulation = async () => {
    setError('');
    try {
      const r = await api.post('/admin/test-data/simulate', { minutes: simMinutes });
      simLogSeen.current = 0;
      setSim({
        runId: r.data.runId, durationMs: r.data.durationMs, elapsedMs: 0,
        status: 'running', counts: { tickets: 0, 'user-activity': 0, 'agent-activity': 0 },
        stopRequested: false,
      });
      pollSimulation(r.data.runId);
    } catch (e) {
      // 409 = már fut egy szimuláció (pl. egy másik lapfülből indítva) —
      // csatlakozzunk hozzá ahelyett, hogy nyers hibát mutatnánk
      if (e.response?.status === 409 && e.response.data?.runId) {
        simLogSeen.current = 0;
        pollSimulation(e.response.data.runId);
        return;
      }
      setError(e.response?.data?.error || t('testlab.runFailed'));
    }
  };

  const stopSimulation = async () => {
    if (!sim?.runId) return;
    try { await api.post(`/admin/test-data/simulate/${sim.runId}/stop`); }
    catch { /* a következő poll úgyis frissíti az állapotot */ }
  };

  const simRunning = sim?.status === 'running';
  const fmtTime = (ms) => {
    const s = Math.max(0, Math.round((ms || 0) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const runDelete = async () => {
    if (!window.confirm(t('testlab.deleteConfirm'))) return;
    setBusy('delete'); setError('');
    try {
      const r = await api.delete('/admin/test-data', { data: { category: delCategory } });
      appendLog(r.data.log, `${t('testlab.deleteTitle')}: ${t(`testlab.categories.${delCategory}`)}`);
      setStats(r.data.stats);
    } catch (e) {
      setError(e.response?.data?.error || t('testlab.runFailed'));
    } finally { setBusy(null); }
  };

  const toggleFlag = async (key) => {
    setBusy('flags'); setError('');
    try {
      const r = await api.put('/admin/test-data/flags', { [key]: !stats?.flags?.[key] });
      setStats(r.data.stats);
    } catch { setError(t('testlab.runFailed')); }
    finally { setBusy(null); }
  };

  const sumObj = (o) => Object.values(o || {}).reduce((a, b) => a + Number(b), 0);

  return (
    <div className="ae-page">
      {error && <div className="ae-msg error">{error}</div>}

      {/* FEJLÉC + STATISZTIKA */}
      <div className="ae-card ae-header-card">
        <div className="ae-topbar">
          <h1 className="ae-title">🧪 {t('testlab.title')}</h1>
          <Button variant="ghost" size="sm" onClick={loadStats}>↻ {t('testlab.refresh')}</Button>
        </div>
        <p style={{ margin: '0 0 0.75rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {t('testlab.intro')} {t('testlab.passwordNote')}: <code>{stats?.test_password || '…'}</code>
        </p>
        {stats && (
          <div className="testlab-stats">
            <span className="testlab-stat">👤 {t('testlab.roles.user')}: <strong>{stats.users.customers}</strong></span>
            <span className="testlab-stat">🧑‍💼 {t('testlab.roles.agent')}: <strong>{stats.users.agents}</strong></span>
            <span className="testlab-stat">🛡️ {t('testlab.roles.admin')}: <strong>{stats.users.admins}</strong></span>
            <span className="testlab-stat">🏡 <strong>{stats.properties}</strong></span>
            <span className="testlab-stat">🤝 <strong>{stats.partners}</strong></span>
            <span className="testlab-stat">📄 <strong>{sumObj(stats.contracts)}</strong></span>
            <span className="testlab-stat">🎫 <strong>{sumObj(stats.cases)}</strong></span>
            {Object.entries(stats.cases || {}).map(([s, n]) => (
              <span key={s} className="ae-badge inactive">{s}: {n}</span>
            ))}
          </div>
        )}
      </div>

      {/* KAPCSOLÓK */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('testlab.flagsTitle')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!stats?.flags?.test_fast_poll}
                   disabled={busy === 'flags'} onChange={() => toggleFlag('test_fast_poll')} />
            <span>
              <strong>{t('testlab.fastPoll')}</strong>
              <br /><small style={{ color: 'var(--text-muted)' }}>{t('testlab.fastPollHint')}</small>
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!stats?.flags?.test_notifications_enabled}
                   disabled={busy === 'flags'} onChange={() => toggleFlag('test_notifications_enabled')} />
            <span>
              <strong>{t('testlab.notif')}</strong>
              <br /><small style={{ color: 'var(--text-muted)' }}>{t('testlab.notifHint')}</small>
            </span>
          </label>
          {stats && !stats.ai_available && (
            <small style={{ color: 'var(--text-muted)' }}>ℹ️ {t('testlab.noAiKey')}</small>
          )}
        </div>
      </div>

      {/* FOLYAMATOS SZIMULÁCIÓ */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">🌊 {t('testlab.sim.title')}</p>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          {t('testlab.sim.intro')}
        </p>

        {!simRunning ? (
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {t('testlab.sim.minutesLabel')}
              <input type="number" min={1} max={5} value={simMinutes}
                onChange={e => setSimMinutes(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
                style={{ width: 56 }} />
            </label>
            <Button variant="solid" disabled={!!busy} onClick={startSimulation}>
              ▶ {t('testlab.sim.start')}
            </Button>
          </div>
        ) : (
          <div>
            <div style={{
              height: 8, borderRadius: 4, background: 'var(--bg-surface-2)',
              overflow: 'hidden', marginBottom: '0.5rem',
            }}>
              <div style={{
                height: '100%', borderRadius: 4,
                background: sim.stopRequested ? 'var(--text-muted)' : 'var(--color-primary)',
                width: `${sim.durationMs ? Math.min(100, Math.round((sim.elapsedMs / sim.durationMs) * 100)) : 0}%`,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              <small style={{ color: 'var(--text-muted)' }}>
                {fmtTime(sim.elapsedMs)} / {fmtTime(sim.durationMs)}
                {sim.stopRequested ? ` — ${t('testlab.sim.stopping')}` : ''}
                {' · '}
                🎫 {sim.counts?.tickets ?? 0} · 🙋 {sim.counts?.['user-activity'] ?? 0} · 🤖 {sim.counts?.['agent-activity'] ?? 0}
              </small>
              <Button variant="danger" size="sm" disabled={sim.stopRequested} onClick={stopSimulation}>
                ■ {t('testlab.sim.stop')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* GENERÁTOROK */}
      <div className="ae-card testlab-card">
        <p className="ae-form-section-title">{t('testlab.generateTitle')}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '0.75rem' }}>
          {GENERATORS.map(g => (
            <div key={g.kind} style={{
              border: '1px solid var(--border-color)', borderRadius: 8, padding: '0.85rem',
              display: 'flex', flexDirection: 'column', gap: '0.55rem',
            }}>
              <strong style={{ fontSize: '0.9rem' }}>{g.icon} {t(`testlab.kinds.${g.kind}`)}</strong>
              <small style={{ color: 'var(--text-muted)', minHeight: '2.2em' }}>{t(`testlab.hints.${g.kind}`)}</small>

              {progress?.kind === g.kind ? (
                <div style={{ marginTop: 'auto' }}>
                  <div style={{
                    height: 6, borderRadius: 3, background: 'var(--bg-surface-2)',
                    overflow: 'hidden', marginBottom: '0.4rem',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: 3, background: 'var(--color-primary)',
                      width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <small style={{ display: 'block', color: 'var(--text-muted)' }}>
                    {progress.done} / {progress.total}
                    {progress.lastLine ? ` — ${progress.lastLine}` : ''}
                  </small>
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginTop: 'auto' }}>
                  <input type="number" min={1} max={g.max} value={counts[g.kind]}
                    onChange={e => setCounts(c => ({ ...c, [g.kind]: Math.max(1, Math.min(g.max, Number(e.target.value) || 1)) }))}
                    style={{ width: 64 }} />
                  {g.hasRole && (
                    <select className="ae-select" value={userRole} onChange={e => setUserRole(e.target.value)}>
                      <option value="user">{t('testlab.roles.user')}</option>
                      <option value="agent">{t('testlab.roles.agent')}</option>
                      <option value="admin">{t('testlab.roles.admin')}</option>
                    </select>
                  )}
                  <Button variant="solid" size="sm" disabled={!!busy || simRunning} onClick={() => runGenerate(g.kind)}>
                    {busy === g.kind ? t('testlab.running') : t('testlab.run')}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* TÖRLÉS */}
      <div className="ae-card testlab-card" style={{ borderColor: 'var(--error-border)' }}>
        <p className="ae-form-section-title" style={{ color: 'var(--error-text)' }}>🗑️ {t('testlab.deleteTitle')}</p>
        <p style={{ margin: '0 0 0.6rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>{t('testlab.deleteHint')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', marginBottom: '0.8rem' }}>
          {DELETE_CATEGORIES.map(cat => (
            <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
              <input type="radio" name="del-category" value={cat}
                     checked={delCategory === cat} onChange={() => setDelCategory(cat)} />
              {t(`testlab.categories.${cat}`)}
            </label>
          ))}
        </div>
        <Button variant="danger" disabled={!!busy || simRunning} onClick={runDelete}>
          {busy === 'delete' ? t('testlab.running') : t('testlab.deleteBtn')}
        </Button>
      </div>

      {/* NAPLÓ */}
      <div className="ae-card testlab-card">
        <div className="ae-topbar" style={{ marginBottom: '0.5rem' }}>
          <p className="ae-form-section-title" style={{ margin: 0 }}>{t('testlab.logTitle')}</p>
          {log.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setLog([])}>{t('testlab.logClear')}</Button>
          )}
        </div>
        {log.length === 0 ? (
          <div className="ae-empty">{t('testlab.logEmpty')}</div>
        ) : (
          <pre style={{
            margin: 0, maxHeight: 340, overflow: 'auto', fontSize: '0.78rem',
            lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: "'Menlo','Consolas',monospace",
          }}>
            {log.join('\n')}
          </pre>
        )}
      </div>
    </div>
  );
};

export default TestLabPage;
